/**
 * Investment Detail Drawer - Shows investment details with cross-family holdings.
 * Opens from FamilyDetail holdings table, GPExposureView, or FamilyClientList.
 */

import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useInvestmentDetail, usePositionMetrics } from "@/hooks/useInvestmentDetail";
import {
  Briefcase,
  Building2,
  Calendar,
  DollarSign,
  ExternalLink,
  Loader2,
  Network,
  TrendingUp,
  Users,
} from "lucide-react";

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-';
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(1)}M`;
  }
  if (value >= 1e3) {
    return `$${(value / 1e3).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

interface InvestmentDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instrumentId: string | null;
  currentFamilyId?: string;
  currentFamilyName?: string;
  positionId?: string;
}

export function InvestmentDetailDrawer({
  open,
  onOpenChange,
  instrumentId,
  currentFamilyId,
  currentFamilyName,
  positionId,
}: InvestmentDetailDrawerProps) {
  const navigate = useNavigate();
  const { data: investment, isLoading: investmentLoading } = useInvestmentDetail(instrumentId);
  const { data: positionMetrics, isLoading: metricsLoading } = usePositionMetrics(positionId || null);

  const isLoading = investmentLoading || metricsLoading;

  // Filter out current family from "other families" list
  const otherFamilies = investment?.familyExposures?.filter(
    (fe) => fe.family_id !== currentFamilyId
  ) || [];

  // Get current family's exposure from the cross-family data (fallback if no positionId)
  const currentFamilyExposure = investment?.familyExposures?.find(
    (fe) => fe.family_id === currentFamilyId
  );

  const handleNavigateToFamily = (familyId: string) => {
    onOpenChange(false);
    navigate(`/firm/families/${familyId}`);
  };

  const handleNavigateToGPExposure = () => {
    onOpenChange(false);
    if (investment?.issuer) {
      navigate(`/firm/gp-exposure`);
    } else {
      navigate('/firm/gp-exposure');
    }
  };

  // Calculate TVPI from position metrics or exposure data
  const calculateTvpi = (nav: number | null, distributed: number | null, paidIn: number | null): string => {
    if (!paidIn || paidIn === 0) return '-';
    const total = (nav || 0) + (distributed || 0);
    return `${(total / paidIn).toFixed(2)}x`;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[500px] sm:w-[600px] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !investment ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Investment not found
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-xl">
                <Briefcase className="h-5 w-5" />
                {investment.name}
              </SheetTitle>
              <SheetDescription className="flex flex-wrap items-center gap-2">
                {investment.issuer && (
                  <Badge variant="outline" className="gap-1">
                    <Building2 className="h-3 w-3" />
                    {investment.issuer}
                  </Badge>
                )}
                {investment.metadata?.strategy && (
                  <Badge variant="secondary">
                    {investment.metadata.strategy}
                  </Badge>
                )}
                {investment.metadata?.vintage_year && (
                  <Badge variant="outline" className="gap-1">
                    <Calendar className="h-3 w-3" />
                    {investment.metadata.vintage_year}
                  </Badge>
                )}
                <Badge variant="outline">
                  {investment.type.replace('_', ' ')}
                </Badge>
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {/* Current Family's Position - only show if we have family context */}
              {currentFamilyId && (
                <div>
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Your Position
                    {currentFamilyName && (
                      <span className="text-muted-foreground font-normal">
                        ({currentFamilyName})
                      </span>
                    )}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-xs text-muted-foreground">Commitment</div>
                        <div className="text-lg font-bold">
                          {formatCurrency(positionMetrics?.commitment_base ?? currentFamilyExposure?.commitment)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-xs text-muted-foreground">NAV</div>
                        <div className="text-lg font-bold">
                          {formatCurrency(positionMetrics?.nav_base ?? currentFamilyExposure?.nav)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-xs text-muted-foreground">Called</div>
                        <div className="text-lg font-bold">
                          {formatCurrency(positionMetrics?.paid_in_base ?? currentFamilyExposure?.paid_in)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-xs text-muted-foreground">TVPI</div>
                        <div className="text-lg font-bold">
                          {positionMetrics?.tvpi
                            ? `${positionMetrics.tvpi.toFixed(2)}x`
                            : calculateTvpi(
                                currentFamilyExposure?.nav || null,
                                null,
                                currentFamilyExposure?.paid_in || null
                              )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              {/* Other Families Holding This Investment */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {currentFamilyId ? 'Other Families Holding This Fund' : 'All Families Holding This Fund'}
                  <Badge variant="secondary" className="ml-auto">
                    {currentFamilyId ? otherFamilies.length : investment.familyCount}
                  </Badge>
                </h3>

                {(currentFamilyId ? otherFamilies : investment.familyExposures).length > 0 ? (
                  <div className="space-y-2">
                    {(currentFamilyId ? otherFamilies : investment.familyExposures).map((fe) => {
                      const tvpi = fe.paid_in > 0 ? fe.nav / fe.paid_in : null;
                      return (
                        <div
                          key={fe.family_id}
                          className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors group"
                          onClick={() => handleNavigateToFamily(fe.family_id)}
                        >
                          <div>
                            <div className="font-medium text-sm flex items-center gap-2">
                              {fe.family_name}
                              <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Commitment: {formatCurrency(fe.commitment)} • NAV: {formatCurrency(fe.nav)}
                              {tvpi && ` • TVPI: ${tvpi.toFixed(2)}x`}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100">
                            View
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground border rounded-lg">
                    {currentFamilyId
                      ? 'Only your family holds this investment'
                      : 'No family exposure data available'}
                  </div>
                )}
              </div>

              {/* Firm-Wide Totals */}
              {investment.familyCount > 0 && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Network className="h-4 w-4" />
                    Firm-Wide Totals
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold">{investment.familyCount}</div>
                      <div className="text-xs text-muted-foreground">Families</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{formatCurrency(investment.totalCommitment)}</div>
                      <div className="text-xs text-muted-foreground">Total Commitment</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{formatCurrency(investment.totalNav)}</div>
                      <div className="text-xs text-muted-foreground">Total NAV</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleNavigateToGPExposure}
                >
                  <TrendingUp className="h-4 w-4" />
                  View GP Exposure
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
